/**
 * Alternative version of `Array.prototype.includes` that allows testing for
 * the existence of an item with a type that is a _superset_ of the type of the
 * items in the array.
 *
 * This allows us to use it to check whether an item of type `string` exists in
 * an array of string literals (e.g: `['foo', 'bar'] as const`) without TypeScript
 * complaining. It will, however, throw a compiler error if you try to pass an item
 * of type `number`.
 *
 * @example
 * const things = ['foo', 'bar'] as const;
 *
 * // error
 * const hasThing = (t: string) => things.includes(t);
 *
 * // compiles
 * const hasThing = (t: string) => includes(things, t);
 *
 * @param array array to search
 * @param item item to search for
 */
var includes = function includes(array, item) {
  return array.includes(item);
};

/**
 * Guard version of `Object.prototype.hasOwnProperty` that makes it
 * possible to access the property being checked (as `unknown`)
 *
 * @param object object to test
 * @param property name of property to test for
 */
var hasOwnProperty = function hasOwnProperty(object, property) {
  return Object.prototype.hasOwnProperty.call(object, property);
};

/**
 * Simple `typeof` check for 'number' as a guard
 */
var isNumber = function isNumber(value) {
  return typeof value === 'number';
};
/**
 * Simple `typeof` check for 'string' as a guard
 */

var isString = function isString(value) {
  return typeof value === 'string';
};
/**
 * Type guard helper for filtering lists that may have empty items
 *
 * @example myList.filter(isDefined)
 *
 * @param input potentially undefined value
 * @returns {boolean} whether input is neither undefined or null
 */

var isDefined = function isDefined(input) {
  return typeof input !== 'undefined' && input !== null;
};
/**
 * Type guard helper for filtering lists that may have falsey items
 *
 * @example myList.filter(isTruthy)
 *
 * @param input potentially falsey value
 * @returns {boolean} whether input is truthy
 */

var isTruthy = function isTruthy(input) {
  return typeof input !== 'undefined' && input !== null && input !== false;
};

/**
 * Get a random integer within a range
 *
 * @param maximum maximum value
 * @param minimum minimum value
 * @returns random integer
 */
var random = function random(maximum, minimum) {
  return Math.floor(Math.random() * (maximum - minimum) + minimum);
};

/**
 * Contruct a type by picking the set of
 * properties with values matching `Value`
 */

/**
 * Contruct a type by converting an object's entries into tuples
 */

/**
 * A version of `Object.entries` that preserves
 * narrower types in the resulting tuple entries
 *
 * @example
 * const o = { a: 1, b: 2 };
 *
 * Object.entries(o) // -> [string, number][]
 * entries(o) // -> ['a' | 'b', number][]
 *
 *
 * ⚠️ NOTE: TypeScript cannot guarantee that an object type
 * cannot have additional properties, so this should only
 * be used when you know that won't be the case
 */
var entries = function entries(o) {
  return Object.entries(o);
};
/**
 * A version of `Object.fromEntries` for immutable sets of entries that
 * preserves a narrower type for the keys of the resulting object
 *
 * @example
 * const e = [['a', 1] as const, ['b', 2] as const];
 *
 * Object.fromEntries(e) // -> { [k: string]: number }
 * fromEntries(e) // -> { [k: 'a' | 'b']: number }
 */

var fromEntries = function fromEntries(e) {
  return Object.fromEntries(e);
};

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

function _iterableToArray(iter) {
  if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter);
}

function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i];

  return arr2;
}

function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

function _toArray(arr) {
  return _arrayWithHoles(arr) || _iterableToArray(arr) || _unsupportedIterableToArray(arr) || _nonIterableRest();
}

//
// ---------- Internal --------------------
//

/**
 * Remove non-word characters from a string
 */
var removeNonWord = function removeNonWord(string) {
  return string.replace(/[^0-9a-zA-Z\xC0-\xFF -]/g, '');
};
/**
 * Format all but the first word of a string with `camelCase`
 *
 * @example
 * ```ts
 * toCamel('some-string');
 * // someString
 *
 * toCamel('Some-string');
 * // SomeString
 */


var toCamel = function toCamel(string) {
  return (// hyphens and underscores to spaces, then strip non-word characters
    removeNonWord(string.replace(/[-_]/g, ' ')).replace(/\s[a-z]/g, function (s) {
      return s.toUpperCase();
    }) // capitalize words
    .replace(/\s+/g, '')
  );
}; // remove spaces
//
// ---------- Public --------------------
//

/**
 * Capitalize the first character in a string
 *
 * @param string string to capitalize
 * @returns new string with first character capitalized
 */


var capitalize = function capitalize(_ref) {
  var _ref2 = _toArray(_ref),
      first = _ref2[0],
      rest = _ref2.slice(1);

  return first.toUpperCase() + rest.join('');
};
/**
 * Format a string with `PascalCase`
 *
 * @param string string to format
 * @returns new string in `PascalCase`
 */

var pascalize = function pascalize(string) {
  return toCamel(string).replace(/^[a-z]/g, function (s) {
    return s.toLocaleUpperCase();
  });
};
/**
 * Format a string with `camelCase`
 *
 * @param string string to format
 * @returns new string in `camelCase`
 */

var camelize = function camelize(string) {
  return toCamel(string).replace(/^[A-Z]/g, function (s) {
    return s.toLocaleLowerCase();
  });
};

export { camelize, capitalize, entries, fromEntries, hasOwnProperty, includes, isDefined, isNumber, isString, isTruthy, pascalize, random };
